import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';

import { useChangeAllowanceTab, useSelector, useToggleProduct } from '../context/hooks';
import {
	selectCanAdvanceAllowanceStep,
	selectInitialProductsTab,
	selectIsProductSelected,
	selectMeter,
	selectProductTabSelectionWarningShown,
} from '../context/selectors';

import { ChoiceCard } from './choice-card';
import { useAppDescriptions, useCollectionDescriptions } from './product-descriptions';
import { StepNav } from './step-nav';
import { Stepper } from './stepper';

import type { BulletOrIconListItem } from './strings';
import type { ProductKey } from '../calculator/types';
import type { ProductCategory } from '../context/types';
import type { SelectableCollectionTypes, SelectableAppTypes } from '../context/types';

export const AllowanceStep = () => {
	const initialTab = useSelector(selectInitialProductsTab);
	const [tab, setTab] = useState<ProductCategory>(initialTab);
	const meter = useSelector(selectMeter);
	const canAdvance = useSelector(selectCanAdvanceAllowanceStep);
	const warningShown = useSelector(selectProductTabSelectionWarningShown);
	const changeTab = useChangeAllowanceTab(tab, setTab);

	const APP_STRINGS = useAppDescriptions();
	const COLLECTION_STRINGS = useCollectionDescriptions();

	const appKeys = useMemo(() => Object.keys(APP_STRINGS) as SelectableAppTypes[], [APP_STRINGS]);
	const collectionKeys = useMemo(
		() => Object.keys(COLLECTION_STRINGS) as SelectableCollectionTypes[],
		[COLLECTION_STRINGS],
	);

	return (
		<>
			<Stepper />
			<h2 css={stepTitleStyle}>Allowance</h2>
			<div css={tabsRowStyle}>
				<button
					css={[tabStyle, tab === 'collection' && tabActiveStyle]}
					onClick={() => changeTab('collection')}
				>
					Collections
				</button>
				<button css={[tabStyle, tab === 'app' && tabActiveStyle]} onClick={() => changeTab('app')}>
					Apps
				</button>
			</div>
			{tab === 'collection' ? (
				<div css={cardRowStyle}>
					{collectionKeys.map((choice: SelectableCollectionTypes) => {
						const { lockupUrl, description, list } = COLLECTION_STRINGS[choice];
						return (
							<CollectionCard
								key={`collections-${choice}`}
								productKey={choice}
								lockupUrl={lockupUrl}
								description={description}
								list={list}
							/>
						);
					})}
				</div>
			) : (
				<div css={gridStyle}>
					{appKeys.map((choice: SelectableAppTypes) => {
						const { label, iconUrl, description } = APP_STRINGS[choice];
						return (
							<AppCard
								key={`apps-${choice}`}
								productKey={choice}
								label={label}
								iconUrl={iconUrl}
								description={description}
								hide={choice === 'jpd' && meter !== 'automations'}
							/>
						);
					})}
				</div>
			)}
			{warningShown && (
				<p css={warningStyle}>
					{tab === 'collection'
						? 'One or more apps were deselected'
						: 'One or more collections were deselected'}
				</p>
			)}
			<StepNav showBack showNext showSkip skipTo="summary" nextDisabled={!canAdvance} />
		</>
	);
};

interface AppCardProps {
	productKey: ProductKey;
	label: string;
	iconUrl: string;
	description: string;
	hide?: boolean;
}

const AppCard = ({ productKey, label, iconUrl, description, hide = false }: AppCardProps) => {
	const isSelected = useSelector(selectIsProductSelected(productKey));
	const toggle = useToggleProduct();
	return (
		<ChoiceCard
			inputType="checkbox"
			value={productKey}
			selected={isSelected}
			onSelect={() => toggle(productKey, !isSelected)}
			groupName="group-apps"
			label={label}
			iconUrl={iconUrl}
			description={description}
			hide={hide}
		/>
	);
};

interface CollectionCardProps {
	productKey: ProductKey;
	lockupUrl: string;
	description: string | ReactNode;
	list: BulletOrIconListItem[];
}

const CollectionCard = ({ productKey, lockupUrl, description, list }: CollectionCardProps) => {
	const isSelected = useSelector(selectIsProductSelected(productKey));
	const toggle = useToggleProduct();
	return (
		<ChoiceCard
			inputType="checkbox"
			value={productKey}
			selected={isSelected}
			onSelect={() => toggle(productKey, !isSelected)}
			groupName="group-collections"
			lockupUrl={lockupUrl}
			description={description}
			list={list}
		/>
	);
};

const stepTitleStyle = css({
	margin: 0,
	fontSize: '1.5rem',
	fontWeight: 700,
	color: wacToken('color.neutral.light1100'),
});

const tabsRowStyle = css({
	display: 'flex',
	gap: token('space.100'),
	borderBlockEnd: `1px solid ${wacToken('color.neutral.light300')}`,
});

const tabStyle = css({
	padding: `${token('space.150')} ${token('space.200')}`,
	fontSize: '0.875rem',
	fontWeight: 600,
	color: wacToken('color.neutral.light800'),
	cursor: 'pointer',
	border: 'none',
	background: 'transparent',
	borderBlockEnd: '2px solid transparent',
});

const tabActiveStyle = css({
	color: wacToken('color.primary.blue'),
	borderBlockEnd: `2px solid ${wacToken('color.primary.blue')}`,
});

const warningStyle = css({
	margin: 0,
	fontSize: '0.875rem',
	fontWeight: 600,
	color: wacToken('color.neutral.light800'),
});

const cardRowStyle = css({
	display: 'flex',
	gap: token('space.400'),
	justifyContent: 'center',
	flexWrap: 'wrap',
});

const gridStyle = css({
	display: 'grid',
	gridTemplateColumns: '1fr 1fr 1fr',
	gap: token('space.400'),
	[media.below.sm]: {
		gridTemplateColumns: '1fr',
	},
});
